import React, { useState, useCallback, useEffect } from 'react';
import { Button, Input, Row, Col, Typography, Checkbox, notification, Modal, Spin } from 'antd';
import { ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import AgGridTable from '../common/AgGridTable';
import moment from 'moment';
import { connect } from 'react-redux';
import { fetchConsumablesLog, updateConsumablesLog, updateDsrLogSections } from '../../redux/dsr/action';

const { Title, Text } = Typography;
const { TextArea } = Input;

// Sample Initial Data based on the UI
const initialData = [
    { id: 1, category: 'test2', name: '-', productCode: 'test_2', quantityAvailable: 10, unit: '-', quantityUsed: 0, additionalRequired: 0 },
    { id: 2, category: 'new1', name: '-', productCode: 'nike1', quantityAvailable: 10001, unit: '-', quantityUsed: 0, additionalRequired: 0 },
    { id: 3, category: 'ss', name: '-', productCode: 'ss', quantityAvailable: 1, unit: '-', quantityUsed: 0, additionalRequired: 0 },
    { id: 4, category: '-', name: '-', productCode: '-', quantityAvailable: 0, unit: '-', quantityUsed: 0, additionalRequired: 0 },
    { id: 5, category: 'consumeble 1500', name: '-', productCode: 'a200', quantityAvailable: 20, unit: '-', quantityUsed: 0, additionalRequired: 0 },
    { id: 6, category: 'consumeble 1600', name: '-', productCode: 'b300', quantityAvailable: 20, unit: '-', quantityUsed: 0, additionalRequired: 0 },
    { id: 7, category: '3/4" static mixer', name: 'Ancilliary', productCode: '9999-19-f687', quantityAvailable: 30, unit: 'TBC', quantityUsed: 0, additionalRequired: 0 },
];

const ConsumablesSection = ({
    shift,
    selectedDate,
    selectedProject,
    selectedMainline,
    currentDsrLog,
    isSkipped,
    isSubmitted,
    onComplete,
    onCancel,
    isShiftSkipped = false,
    onSkipChange,
    // Redux props
    onFetchConsumablesLog,
    onUpdateConsumablesLog,
    onUpdateDsrLogSections,
    isConsumablesLogLoading,
    isUpdatingConsumablesLog,
    currentConsumablesLog,
}) => {
    const [rowData, setRowData] = useState([]);
    const [comments, setComments] = useState('');
    const [skipped, setSkipped] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    // Format the selected date or use today's date as fallback
    const displayDate = selectedDate ? moment(selectedDate).format('DD-MMM-YYYY') : moment().format('DD-MMM-YYYY');

    // Convert shift string to number for API
    const getShiftNumber = (shiftStr) => {
        return shiftStr === 'day' ? 0 : 1;
    };

    // Convert API data to table format
    const convertApiDataToTableFormat = (apiData) => {
        if (!apiData || !apiData.data || !apiData.data.data) {
            return [];
        }

        const convertedData = apiData.data.data.map((item, index) => ({
            id: item.id,
            projectConsumableId: item.projectConsumableId,
            category: item.category || '-',
            name: item.consumableName || '-',
            productCode: item.productCode || '-',
            quantityAvailable: item.pendingQuantityInStock || 0,
            unit: item.unit || '-',
            quantityUsed: item.quantityUsed || 0,
            additionalRequired: item.additionalRequired || 0,
        // Store original API data for updates
            apiData: item
        }));

        return convertedData;
    };

    // Fetch consumables log when component mounts or dependencies change
    useEffect(() => {
        if (selectedProject && selectedMainline && selectedDate) {
            const userEmail = JSON.parse(localStorage.getItem('user')).email;
            const logDate = moment(selectedDate).format('YYYY-MM-DD');
            const shiftNumber = getShiftNumber(shift);

            onFetchConsumablesLog({
                projectId: selectedProject.id,
                logDate: logDate,
                userEmail: userEmail,
                shift: shiftNumber,
                sowId: selectedMainline.id
            });
        }
    }, [selectedProject, selectedMainline, selectedDate, shift, onFetchConsumablesLog]);

    // Update local state when API data is received
    useEffect(() => {
        if (currentConsumablesLog) {
            const convertedData = convertApiDataToTableFormat(currentConsumablesLog);
            setRowData(convertedData);
            setIsDirty(false);
        }
    }, [currentConsumablesLog]);

    // Update skip state from props
    useEffect(() => {
        setSkipped(isSkipped || false);
    }, [isSkipped]);

    // Reset dirty state when date changes (new data should be loaded)
    useEffect(() => {
        setIsDirty(false);
    }, [selectedDate]);

    // Reset skip state when section or shift changes
    useEffect(() => {
        setSkipped(isSkipped || false);
    }, [isSkipped, shift, selectedDate]);

    // Clear local state when component unmounts
    useEffect(() => {
        return () => {
            setRowData([]);
            setComments('');
            setSkipped(false);
            setIsDirty(false);
        };
    }, []);

    const columnDefs = [
        { headerName: 'Consumable Category', field: 'category', editable: false, flex: 1 },
        { headerName: 'Name', field: 'name', editable: false, flex: 1 },
        { headerName: 'Product Code', field: 'productCode', editable: false, flex: 1 },
        { headerName: 'Quantity Available', field: 'quantityAvailable', type: 'numericColumn', editable: false, flex: 1 },
        { headerName: 'Unit', field: 'unit', editable: false, flex: 1 },
        {
            headerName: 'Quantity Used',
            field: 'quantityUsed',
            type: 'numericColumn',
            editable: (params) => {
                const stock = params.data?.quantityAvailable ?? 0;
                return !skipped && !isSubmitted && Number(stock) > 0;
            },
            flex: 1,
            suppressClickEdit: false,
            cellStyle: params => {
                const stock = params.data?.quantityAvailable ?? 0;
                const canEdit = !skipped && !isSubmitted && Number(stock) > 0;
                if (canEdit) {
                    return {
                        backgroundColor: 'rgb(50, 29, 30)',
                        borderRadius: '8px',
                        border: '1px solid #666',
                        transition: 'all 0.3s ease'
                    };
                }
                return null;
            },
            cellClass: params => {
                const stock = params.data?.quantityAvailable ?? 0;
                const canEdit = !skipped && !isSubmitted && Number(stock) > 0;
                if (canEdit) return 'editable-cell-highlight';
                return null;
            }
        },
        {
            headerName: 'Additional Required',
            field: 'additionalRequired',
            type: 'numericColumn',
            editable: true,
            flex: 1,
            suppressClickEdit: false,
            cellStyle: params => {
                if (params.colDef.editable && !skipped && !isSubmitted) {
                    return {
                        backgroundColor: 'rgb(50, 29, 30)',
                        borderRadius: '8px',
                        border: '1px solid #666',
                        transition: 'all 0.3s ease'
                    };
                }
                return null;
            },
            cellClass: params => {
                if (params.colDef.editable && !skipped && !isSubmitted) {
                    return 'editable-cell-highlight';
                }
                return null;
            }
        },
    ];

    const onCellValueChanged = useCallback((params) => {
        const { data, field, newValue } = params;

        // Do not allow used qty when consumable stock is zero (same as mobile app restriction)
        if (field === 'quantityUsed') {
            const stock = Number(data.quantityAvailable ?? 0);
            if (stock <= 0) {
                notification.warning({
                    message: 'Cannot enter used quantity',
                    description: 'Used quantity in shift is not allowed when consumable stock is zero.',
                    placement: 'bottomRight',
                });
                params.node.setDataValue('quantityUsed', 0);
                return;
            }
        }

        setIsDirty(true);
        setRowData(prevData => {
            const updatedData = prevData.map(row =>
                row.id === params.data.id ? { ...row, ...params.data } : row
            );
            return updatedData;
        });
    }, []);

    const handleCommentsChange = (e) => {
        setComments(e.target.value);
        setIsDirty(true);
    };

    const handleSkipChange = (e) => {
        const newSkipState = e.target.checked;
        setSkipped(newSkipState);
        setIsDirty(true);

        // Use the parent component's centralized skip handler
        if (onSkipChange) {
            onSkipChange(e);
        } else {
            // Fallback to local handling if no parent handler provided
            if (currentDsrLog?.id) {
                const submissionStatus = generateSubmissionStatus('consumables', shift, true);
                const sectionsSkipped = {
                    ...currentDsrLog.sectionsSkipped,
                    isConsumablesSkippedDayShift: shift === 'day' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isConsumablesSkippedDayShift || 0),
                    isConsumablesSkippedNightShift: shift === 'night' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isConsumablesSkippedNightShift || 0),
                };

                onUpdateDsrLogSections({
                    dsrLogId: currentDsrLog.id,
                    submissionStatus: submissionStatus,
                    sectionsSkipped: sectionsSkipped
                });
            }
        }
    };

    // Helper function to generate submission status fields for a specific section and shift
    const generateSubmissionStatus = (section, shift, isSubmitted = true) => {
        const shiftKeySuffix = shift === 'day' ? 'Dayshift' : 'Nightshift';
        let submittedKey;

        switch (section) {
            case 'technician':
                submittedKey = `isTechnicianLogSubmitted${shiftKeySuffix}`;
                break;
            case 'downtime':
                submittedKey = `isDowntimeSubmitted${shiftKeySuffix}`;
                break;
            case 'consumables':
                submittedKey = `isConsumablesSubmitted${shiftKeySuffix}`;
                break;
            case 'equipment':
                submittedKey = `isEquipmentUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'ehs':
                submittedKey = `isEhsIssuesSubmitted${shiftKeySuffix}`;
                break;
            case 'material':
                submittedKey = `isMaterialUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'production':
                submittedKey = `isProductionJointSubmitted${shiftKeySuffix}`;
                break;
            default:
                return {};
        }

        return { [submittedKey]: isSubmitted };
    };

    const handleSave = async () => {
        if (skipped) {
            // If skipped, just complete without saving data to API
            if (onComplete) onComplete(true, 'consumables');
            return;
        }

        try {
        // Prepare data for API
            const dsrLogConsumables = rowData.map(row => ({
                dsrLogId: currentDsrLog?.id,
                projectConsumableId: row.projectConsumableId,
                quantityUsed: row.quantityUsed || 0,
                additionalRequired: row.additionalRequired || 0,
                shift: getShiftNumber(shift)
            }));

            const payload = {
                dsrLogConsumables
            };

            await onUpdateConsumablesLog(payload);
            setIsDirty(false);

            // Update submission status and sectionsSkipped for this section
            const submissionStatus = generateSubmissionStatus('consumables', shift, true);
            const sectionsSkipped = {
                ...currentDsrLog.sectionsSkipped,
                isConsumablesSkippedDayShift: shift === 'day' ? 0 : (currentDsrLog.sectionsSkipped?.isConsumablesSkippedDayShift || 0),
                isConsumablesSkippedNightShift: shift === 'night' ? 0 : (currentDsrLog.sectionsSkipped?.isConsumablesSkippedNightShift || 0)
            };

            await onUpdateDsrLogSections({
                dsrLogId: currentDsrLog?.id,
                submissionStatus: submissionStatus,
                sectionsSkipped: sectionsSkipped
            });

            // Only navigate to next section on successful save
            if (onComplete) onComplete(true, 'consumables');

            // Refresh data after successful save
            if (selectedProject && selectedMainline && selectedDate) {
                const userEmail = JSON.parse(localStorage.getItem('user')).email;
                const logDate = moment(selectedDate).format('YYYY-MM-DD');
                const shiftNumber = shift === 'day' ? '0' : '1';

                onFetchConsumablesLog({
                    projectId: selectedProject.id,
                    logDate: logDate,
                    userEmail: userEmail,
                    shift: shiftNumber,
                    sowId: selectedMainline.id
                });
            }

        } catch (error) {
            console.error('Error saving consumables log:', error);
            notification.error({
                message: 'Save Failed',
                description: 'Failed to save consumables data. Please try again.'
            });
            // Stay in the same section on error - button will be re-enabled
        }
    };

    const handleNext = () => {
        if (isDirty) {
            Modal.confirm({
                title: 'Warning!',
                icon: <ExclamationCircleOutlined />,
                content: 'You have made changes to the table. Do you want to save them?',
                okText: 'Save',
                cancelText: 'Discard',
                onOk: () => {
                    handleSave();
                    if (onComplete) onComplete(!skipped);
                },
                onCancel: () => {
                    if (onComplete) onComplete(!skipped);
                }
            });
        } else {
            if (onComplete) onComplete(!skipped);
        }
    };

    return (
        <div style={{ padding: 24, position: 'relative' }}>
            <Spin
                spinning={isConsumablesLogLoading}
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                tip="Loading consumables data..."
                size="large"
            >
                <div style={{ marginBottom: 16, height: '40vh', overflow: 'auto' }}>
                    <AgGridTable
                        rowData={rowData}
                        columnDefs={columnDefs.map(col => ({
                            ...col,
                            editable: typeof col.editable === 'function' ? col.editable : (col.editable === false ? false : !skipped && !isSubmitted)
                        }))}
                        onCellValueChanged={onCellValueChanged}
                        theme="ag-theme-material-dark"
                        loading={isConsumablesLogLoading}
                    />
                </div>

                <div style={{ marginBottom: 24 }}>
                    <Title level={5} style={{ textAlign: 'left' }}>Comments</Title>
                    <TextArea
                        rows={3}
                        placeholder="Please add your comments here"
                        value={comments}
                        onChange={handleCommentsChange}
                        disabled={skipped}
                    />
                </div>
            </Spin>

            {/* Fixed Save/Cancel Buttons */}
            <div style={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                zIndex: 1000,
                display: 'flex',
                gap: 12
            }}>
                <Button
                    onClick={onCancel}
                    size="large"
                    shape="round"
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    type="primary"
                    size="large"
                    shape="round"
                    disabled={isUpdatingConsumablesLog}
                    loading={isUpdatingConsumablesLog}
                >
                    Save & Next
                </Button>
            </div>
        </div>
    );
};

const mapStateToProps = ({ dsrReducer }) => ({
    isConsumablesLogLoading: dsrReducer.isConsumablesLogLoading,
    isUpdatingConsumablesLog: dsrReducer.isUpdatingConsumablesLog,
    currentConsumablesLog: dsrReducer.currentConsumablesLog,
});

const mapDispatchToProps = (dispatch) => ({
    onFetchConsumablesLog: (payload) => dispatch(fetchConsumablesLog(payload)),
    onUpdateConsumablesLog: (payload) => dispatch(updateConsumablesLog(payload)),
    onUpdateDsrLogSections: (payload) => dispatch(updateDsrLogSections(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ConsumablesSection); 